import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import ToastActions from 'redux/toast';
import {
  EUser,
  ERate,
  ENotice,
  EOrganization,
  ESnapshotExists,
  exists
} from 'lib/types';
import { LineItem } from 'lib/types/invoices';
import { FormStep } from 'components/forms';
import {
  calculateInvoicePricing,
  getConvenienceFeeWithRepFeeFromRateOrNewspaper,
  getDistributeFeeSettings,
  getInvoiceLineItems,
  shouldDistributeFee
} from 'lib/pricing';
import { dbToUICurrencyString } from 'lib/pricing/ui';
import LoadingState from 'components/LoadingState';
import {
  getCurrencySymbol,
  getDueDate,
  getNoticeIsInvoicedOutsideColumn,
  getNoticeRequiresUpfrontPayment
} from 'lib/helpers';
import {
  getAddressFromCustomerInfo,
  getCustomer,
  getCustomerName,
  getCustomerOrganization
} from 'lib/notice/customer';
import { getFirebaseContext } from 'utils/firebase';
import { isAffidavitDisabled } from 'lib/affidavits';
import { getRecipientName } from 'lib/utils/invoices';
import { useNoticeAffidavitSettings } from 'lib/frontend/hooks/useNoticeAffidavitSettings';
import { getMailDataFromNoticeOrDraft } from 'lib/mail';
import {
  excludeUnmailedInvoiceCopies,
  isMailedInvoiceAddOnLineItem
} from 'lib/pricing/noticeAddOns/invoice';
import { Modal } from 'lib/components/Modal';
import { isEqual } from 'lodash';
import { safeStringify } from 'lib/utils/stringify';
import { logAndCaptureCriticalError, logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import { safeAsync } from 'lib/safeWrappers';
import {
  selectActiveOrganization,
  selectIsPublisher,
  selectUser
} from 'redux/auth';
import {
  INVOICE_CREATION_IN_PROGRESS_ERROR,
  NOTICE_HAS_INVOICE_ERROR
} from 'services/billing';
import { Alert } from 'lib/components/Alert';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { createNoticeInvoice } from '../handleInvoices';
import InvoiceFormCustomerTable from './InvoiceFormCustomerTable';
import InvoiceFormPaperInfoTable from './InvoiceFormPaperInfoTable';
import InvoiceFormTable from './InvoiceFormTable';
import InvoiceFormTotalDueTable from './InvoiceFormTotalDueTable';
import InvoiceFormDueDate from './InvoiceFormDueDate';
import InvoiceConfirm from './InvoiceConfirm';
import copy from './copy';
import InvoiceFormMemo from './InvoiceFormMemo';
import NoticePreview from '../../noticePreview';
import { useCheckInvoiceCreationInitiated } from './useCheckInvoiceCreationInitiated';
import { getNoticeContentRevisionMillis } from './helpers';

type InvoiceFormProps = {
  forward: () => void;
  forwardLabel: string;
  back: () => void;
  advertiserSnap: ESnapshotExists<EUser>;
  advertiserOrganization: ESnapshotExists<EOrganization> | null;
  rateSnap: ESnapshotExists<ERate>;
  noticeSnap: ESnapshotExists<ENotice>;
  newspaperSnap: ESnapshotExists<EOrganization>;
  idempotencyKey: string;
};

function InvoiceForm({
  forward,
  forwardLabel,
  back,
  advertiserSnap,
  advertiserOrganization,
  rateSnap,
  noticeSnap,
  newspaperSnap,
  idempotencyKey
}: InvoiceFormProps) {
  const user = useAppSelector(selectUser);
  const isPublisher = useAppSelector(selectIsPublisher);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const { postWithoutFormatting, pricing } = noticeSnap.data();

  const emptyLineItems: LineItem[] = [
    { amount: 0, date: new Date(), type: null }
  ];

  const initialLineItems = postWithoutFormatting
    ? emptyLineItems
    : pricing
    ? getInvoiceLineItems(pricing, newspaperSnap, rateSnap)
    : [];

  const [lineItems, setLineItems] = useState<LineItem[]>(initialLineItems);
  const [taxPercent, setTaxpercent] = useState(
    postWithoutFormatting ? 0 : pricing?.taxPct ?? 0
  );
  const [openModal, setOpenModal] = useState(false);
  const [customMemo, setCustomMemo] = useState('');

  const [customId, setCustomId] = useState(noticeSnap.data().customId || '');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(
    noticeSnap.data().purchaseOrderNumber || ''
  );
  const [invoiceOutsideColumn, setInvoiceOutsideColumn] = useState(false);
  const [dueDate, setDueDate] = useState<number>();
  const [requireUpfrontPayment, setRequireUpfrontPayment] = useState<boolean>();
  const [billingSettingsReady, setBillingSettingsReady] = useState(false);
  const [isRefreshingDueDate, setIsRefreshingDueDate] = useState(false);
  const [isConfirmingInvoice, setIsConfirmingInvoice] = useState(false);
  const submitInFlightRef = useRef(false);

  const invoiceCreationInitiated = useCheckInvoiceCreationInitiated(noticeSnap);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!pricing) {
      const error = new Error('Pricing data is not ready on the notice');
      logAndCaptureCriticalError(
        ColumnService.WEB_PLACEMENT,
        error,
        '[Old Create Invoice]: Pricing data missing',
        { noticeId: noticeSnap.id }
      );
      return;
    }
    if (!isEqual(initialLineItems, lineItems)) {
      setLineItems(initialLineItems);
    }
  }, [safeStringify(pricing)]);

  const handleChange = (i: number, amountInCents: number) => {
    const newValues = [...lineItems];

    // Max price for a line item is $35,000 (likely arbitrary?)
    const MAX_LINE_ITEM_AMOUNT_IN_CENTS = 65000 * 100;
    newValues[i].amount = Math.min(
      MAX_LINE_ITEM_AMOUNT_IN_CENTS,
      Math.round(amountInCents)
    );
    setLineItems(newValues);
  };

  const refreshDueDateForUpfrontPayment = async (
    nextRequireUpfrontPayment?: boolean
  ) => {
    setIsRefreshingDueDate(true);
    try {
      const nextDueDate = await getDueDate(
        getFirebaseContext(),
        noticeSnap,
        nextRequireUpfrontPayment
      );

      setDueDate(nextDueDate);

      const nextDueDateMoment = moment(nextDueDate * 1000);
      if (moment().isAfter(nextDueDateMoment)) {
        setRequireUpfrontPayment(false);
      }
    } finally {
      setIsRefreshingDueDate(false);
    }
  };

  const handleRequireUpfrontPaymentChange = (
    nextRequireUpfrontPayment: boolean
  ) => {
    setRequireUpfrontPayment(nextRequireUpfrontPayment);
    void refreshDueDateForUpfrontPayment(nextRequireUpfrontPayment);
  };

  useEffect(() => {
    void (async () => {
      setBillingSettingsReady(false);
      const ctx = getFirebaseContext();
      const [resolvedRequireUpfrontPayment, shouldInvoiceOutsideColumn] =
        await Promise.all([
          getNoticeRequiresUpfrontPayment(ctx, noticeSnap),
          getNoticeIsInvoicedOutsideColumn(ctx, noticeSnap)
        ]);

      setRequireUpfrontPayment(resolvedRequireUpfrontPayment);
      setInvoiceOutsideColumn(!!shouldInvoiceOutsideColumn);
      await refreshDueDateForUpfrontPayment(resolvedRequireUpfrontPayment);
      setBillingSettingsReady(true);
    })();
  }, [noticeSnap.id]);

  const { value: billTo } = useAsyncEffect({
    fetchData: async () => {
      const { invoiceRecipient } = noticeSnap.data();

      const billToEmail =
        (invoiceRecipient?.type === 'email'
          ? invoiceRecipient.email
          : undefined) ||
        advertiserOrganization?.data().email ||
        advertiserSnap.data().email;

      if (invoiceRecipient) {
        return {
          billToName: getRecipientName(invoiceRecipient),
          billingAddress:
            invoiceRecipient.type === 'mail' ? invoiceRecipient.address : null,
          billToEmail
        };
      }

      const customerOrg = await getCustomerOrganization(
        getFirebaseContext(),
        advertiserOrganization ?? undefined,
        newspaperSnap
      );
      const customer = await getCustomer(
        getFirebaseContext(),
        advertiserSnap,
        newspaperSnap
      );

      const billToName = advertiserOrganization
        ? // Why are we not reading the customer organization here?
          advertiserOrganization.data().name
        : getCustomerName(customer, advertiserSnap, false) || '';

      return {
        billToName,
        billingAddress: getAddressFromCustomerInfo(
          customerOrg ?? undefined,
          customer ?? undefined,
          advertiserSnap
        ),
        billToEmail
      };
    },
    errorConfig: {
      service: ColumnService.PAYMENTS,
      message: 'Failed to get customer for invoice creation',
      tags: { noticeId: noticeSnap.id, userId: user?.id || '' }
    },
    dependencies: [
      safeStringify(noticeSnap.data().invoiceRecipient),
      advertiserSnap.id,
      advertiserOrganization?.id,
      newspaperSnap.id
    ]
  });

  const { value: mail } = useAsyncEffect({
    fetchData: () => getMailDataFromNoticeOrDraft(noticeSnap.ref),
    errorConfig: {
      service: ColumnService.PAYMENTS,
      message: 'Failed to get mail data for invoice creation',
      tags: { noticeId: noticeSnap.id, userId: user?.id || '' }
    },
    dependencies: [
      noticeSnap.id,
      newspaperSnap?.id,
      getNoticeContentRevisionMillis(noticeSnap)
    ]
  });

  const billedMail = useMemo(
    () => excludeUnmailedInvoiceCopies(mail ?? [], invoiceOutsideColumn),
    [mail, invoiceOutsideColumn]
  );

  const endSubmission = () => {
    submitInFlightRef.current = false;
    setIsConfirmingInvoice(false);
    setOpenModal(false);
  };

  const reportSubmitFailure = (error: Error) => {
    logAndCaptureException(
      ColumnService.PAYMENTS,
      error,
      '[Old Create Invoice]: Failed to create a new invoice',
      { noticeId: noticeSnap.id }
    );
    dispatch(
      ToastActions.toastError({
        headerText: 'Failed to create an invoice',
        bodyText: [
          INVOICE_CREATION_IN_PROGRESS_ERROR,
          NOTICE_HAS_INVOICE_ERROR
        ].some(msg => error.message.includes(msg))
          ? error.message
          : 'Please try again or contact help@column.us'
      })
    );
  };

  // An invoice billed outside Column is never mailed, so its mailed-invoice
  // add-on is not billed. Full state is kept so toggling IOC back off restores it.
  const billedLineItems = useMemo(
    () =>
      lineItems
        .map((lineItem, sourceIndex) => ({ lineItem, sourceIndex }))
        .filter(
          ({ lineItem }) =>
            !invoiceOutsideColumn || !isMailedInvoiceAddOnLineItem(lineItem)
        ),
    [lineItems, invoiceOutsideColumn]
  );

  const handleConfirm = async () => {
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    setIsConfirmingInvoice(true);

    /**
     * Greetings future code prospectors! You may notice that we are
     * updating the custom ID on the notice twice when creating an invoice:
     * here, and again upon completion of invoice creation (see
     * functions/src/invoices/helper.ts). This may seem redundant, but please
     * be aware that this does help us evade a race condition between
     * manually entered order numbers at invoice creation and auto-generated
     * order numbers (see ONCALL-2814 and getShouldAutoGenerateOrderNumsForNotice).
     * This should be kept in mind if the below update is to be removed!
     */
    if (customId) {
      const [updateError] = await safeAsync(() =>
        noticeSnap.ref.update({ customId })
      )();
      if (updateError) {
        endSubmission();
        reportSubmitFailure(updateError);
        return;
      }
    }

    const [error] = await createNoticeInvoice({
      lineItems: billedLineItems.map(({ lineItem }) => lineItem),
      inAppTaxPct: taxPercent,
      noticeSnap,
      advertiserSnap,
      newspaperSnap,
      customId,
      purchaseOrderNumber,
      customMemo,
      dueDate,
      requireUpfrontPayment,
      invoiceOutsideColumn,
      idempotencyKey,
      user
    });

    endSubmission();

    if (error) {
      reportSubmitFailure(error);
      return;
    }
    forward();
  };

  const isFeeDistributed = shouldDistributeFee(
    getDistributeFeeSettings(newspaperSnap, rateSnap)
  );

  const affidavitReconciliationSettings = useNoticeAffidavitSettings(
    getFirebaseContext(),
    noticeSnap
  );
  // Without the notice-level override: add-on pricing keys its "publisher
  // covers the first wet-sig copy" waiver off what the paper requires, not off
  // a wet-sig setting the customer elected on this notice.
  const paperLevelAffidavitReconciliationSettings = useNoticeAffidavitSettings(
    getFirebaseContext(),
    noticeSnap,
    { skipNoticeLevelSettings: true }
  );

  const inAppLineItems = postWithoutFormatting
    ? emptyLineItems
    : billedLineItems.map(({ lineItem }) => lineItem);
  const convenienceFee = getConvenienceFeeWithRepFeeFromRateOrNewspaper(
    newspaperSnap,
    rateSnap
  );
  const affidavitDisabled = isAffidavitDisabled(
    noticeSnap.data(),
    newspaperSnap
  );

  const invoicePricing = calculateInvoicePricing(newspaperSnap, {
    advertiserOrganization,
    inAppLineItems,
    affidavitReconciliationSettings,
    paperLevelAffidavitReconciliationSettings,
    requiresAffidavit: !affidavitDisabled,
    inAppTaxPct: taxPercent,
    convenienceFee,
    mail: billedMail,
    noticePublicationDates: noticeSnap.data().publicationDates,
    rate: rateSnap
  });

  const {
    totalInCents,
    affidavitFeeInCents,
    convenienceFeeInCents,
    subtotalInCents
  } = invoicePricing;

  const processingFeeInCents = affidavitFeeInCents + convenienceFeeInCents;

  const belowMinimum =
    !isNaN(subtotalInCents) &&
    exists(rateSnap) &&
    subtotalInCents < rateSnap.data().minimum;

  const noticeDoesNotHaveInvoice = !noticeSnap.data().invoice;
  const pricingReady = postWithoutFormatting || !!noticeSnap.data().pricing;

  const isBillingControlsLoading = !billingSettingsReady || isRefreshingDueDate;
  const isPageLoading = !exists(newspaperSnap) || !activeOrganization;

  const stepComplete =
    pricingReady &&
    !belowMinimum &&
    noticeDoesNotHaveInvoice &&
    !invoiceCreationInitiated &&
    !isBillingControlsLoading;

  if (isPageLoading) {
    return <LoadingState context={{ location: 'InvoiceForm' }} />;
  }

  const handleTaxPercentChange = (e: React.ChangeEvent<any>) => {
    const pct = e.target.value;
    setTaxpercent(+parseFloat(pct).toFixed(2));
  };

  const getTermsText = (
    { showDistributeMessage } = { showDistributeMessage: false }
  ) => {
    const { invoiceRecipient } = noticeSnap.data();
    const role = invoiceRecipient ? 'invoice recipient' : 'advertiser';

    return `The ${role} ${
      billTo?.billToName ? `(${billTo?.billToName})` : ''
    } will be billed for the advertisement plus taxes and fees. ${
      copy.enoticeName
    } will fill this invoice and pay ${
      newspaperSnap.data().name
    } net 30 from when ${
      affidavitDisabled
        ? 'payment is received.'
        : 'the proof of publication is uploaded.'
    }${
      isFeeDistributed && showDistributeMessage
        ? '</br></br>Column processing fees are for your reference only and will not appear as its own line item in the invoice. You can modify this under Rates Settings.'
        : ''
    }`;
  };

  const currency = getCurrencySymbol(newspaperSnap.data().currency);
  const ianaTimezone = newspaperSnap.data().iana_timezone;

  return (
    <>
      <div className="bg-gray-100">
        {!pricingReady && (
          <Alert
            id="pricing-not-ready-alert"
            status="error"
            title="Invoice cannot be generated. Please edit and resave this notice and try again."
          />
        )}
        <div
          id="notice-details-page"
          className="max-w-full mx-auto sm:px-6 lg:px-8 lg:pr-0 sm:pr-0"
        >
          <div className="grid grid-cols-8 grid-flow-col gap-8 -ml-8">
            <div className="col-span-8 md:col-span-5 pb-20 overflow-y-scroll h-screen -mr-8">
              <FormStep
                title={'Create Invoice'}
                stepComplete={stepComplete}
                forward={() => {
                  setOpenModal(true);
                }}
                id="invoice-form"
                forwardLabel={forwardLabel}
                back={back}
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <InvoiceFormPaperInfoTable
                    organization={newspaperSnap.data()}
                  />
                  <InvoiceFormCustomerTable billTo={billTo} />
                </div>
                <div className="mt-4">
                  <InvoiceFormTable
                    handleChange={(index, amountInCents) =>
                      handleChange(
                        billedLineItems[index].sourceIndex,
                        amountInCents
                      )
                    }
                    values={billedLineItems.map(({ lineItem }) => lineItem)}
                    currency={currency}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {noticeSnap.data().adTemplate && (
                    <InvoiceFormMemo
                      terms={getTermsText({ showDistributeMessage: true })}
                      noticeSnap={noticeSnap}
                      rateSnap={rateSnap}
                      setCustomMemo={setCustomMemo}
                      customMemo={customMemo}
                      purchaseOrderNumber={purchaseOrderNumber}
                      setPurchaseOrderNumber={setPurchaseOrderNumber}
                      currency={currency}
                      setCustomId={setCustomId}
                      customId={customId}
                    />
                  )}
                  <div>
                    <InvoiceFormTotalDueTable
                      handleTaxPercentChange={handleTaxPercentChange}
                      subtotalInCents={subtotalInCents}
                      belowMinimum={belowMinimum}
                      rateMinimum={dbToUICurrencyString(
                        rateSnap.data().minimum
                      )}
                      taxPercent={taxPercent}
                      totalDueInCents={totalInCents}
                      currency={currency}
                      processingFeeInCents={processingFeeInCents}
                      isFeeDistributed={isFeeDistributed}
                    />
                    <InvoiceFormDueDate
                      newspaper={newspaperSnap}
                      noticeSnap={noticeSnap}
                      setDueDate={setDueDate}
                      dueDate={dueDate}
                      requireUpfrontPayment={requireUpfrontPayment}
                      invoiceOutsideColumn={invoiceOutsideColumn}
                      setInvoiceOutsideColumn={setInvoiceOutsideColumn}
                      ianaTimezone={ianaTimezone}
                      isBillingDataLoading={isBillingControlsLoading}
                      onRequireUpfrontPaymentChange={
                        handleRequireUpfrontPaymentChange
                      }
                    />
                  </div>
                </div>
              </FormStep>
            </div>
            <div
              id="sidebar"
              className="hidden md:block md:col-span-3 h-screen z-10"
            >
              <div className="bg-gray-800 h-full">
                <NoticePreview
                  newspaper={newspaperSnap}
                  isPublisher={!!isPublisher}
                  notice={noticeSnap}
                  showAffinityXSyncPanel={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {openModal && (
        <Modal
          id={'submit-invoice-modal'}
          title="Review and Submit Invoice"
          onClose={() => setOpenModal(false)}
        >
          <p className="mb-4">
            This invoice is due
            {noticeSnap.data().invoiceRecipient ? ' ' : ' by the advertiser '}
            on
            <b className="ml-1">
              {dueDate && ianaTimezone
                ? moment(dueDate * 1000)
                    .tz(ianaTimezone)
                    .format('MMMM D, YYYY')
                : ''}
            </b>
          </p>
          <InvoiceConfirm
            getTermsText={getTermsText}
            handleConfirm={handleConfirm}
            isConfirmingInvoice={isConfirmingInvoice}
            disabled={!stepComplete}
          />
        </Modal>
      )}
    </>
  );
}

export default InvoiceForm;
